<template>
  <!-- 二级侧边栏菜单项容器 -->
  <li
    ref="routerItem"
    v-auto-animate
    class="w-[100%] cursor-pointer"
  >
    <!-- 有子菜单的情况：显示可展开的父级菜单项 -->
    <div
      v-if="showChildren"
      class="nav-item justify-between"
      @click="() => showChildren && toggleExpended()"
    >
      <!-- 左侧：图标 + 标题 -->
      <div class="flex items-center">
        <!-- SVG图标显示 -->
        <SvgIcon
          v-if="router?.meta?.icon"
          :key="router?.meta?.icon"
          class="mr-[8px] inline-block text-white"
          :name="router?.meta?.icon"
          size="16"
        />
        <!-- 菜单标题文本 -->
        <Text
          overflow
          tool-tip
          type="navTitle"
          class="font-bold text-white force-white-text"
          :content="router?.meta?.title || router?.meta?.name"
        />
      </div>
      <!-- 右侧：展开/收缩箭头图标 -->
      <t-icon
        v-if="showChildren"
        class="w-[14px] transition text-white"
        :class="{ '-rotate-90': !expended }"
        name="chevron-down"
      />
    </div>

    <!-- 无子菜单的情况：显示可点击的路由链接 -->
    <router-link
      v-else
      class="nav-item justify-between"
      :class="{
        'router-link-active': active === router.name,
      }"
      :to="router"
    >
      <!-- 左侧：图标 + 标题 -->
      <div class="flex items-center">
        <!-- SVG图标显示 -->
        <SvgIcon
          v-if="router?.meta?.icon"
          :key="router?.meta?.icon"
          class="mr-[8px] inline-block text-white"
          :name="router?.meta?.icon"
          size="16"
        />
        <!-- 菜单标题文本（无子菜单时为白色） -->
        <Text
          overflow
          tool-tip
          type="navTitle"
          class="font-bold text-white force-white-text"
          :content="router?.meta?.title || router?.meta?.name"
        />
      </div>
    </router-link>

    <!-- 子菜单列表：仅在展开状态且有子菜单时显示 -->
    <ul
      v-if="expended && showChildren"
      class="mt-[10px] space-y-[10px]"
    >
      <!-- 遍历渲染每个子菜单项 -->
      <li
        v-for="(subRouter, i) in visibleChildren"
        :key="`${router.meta?.name}-${subRouter.meta?.name || i}`"
        class="relative"
        @click.stop
      >
        <!-- 子菜单路由链接 -->
        <router-link
          class="nav-item pl-[32px] text-white"
          :to="subRouter"
        >
          <!-- 子菜单标题文本（修改为白色） -->
          <Text
            overflow
            tool-tip
            size="normal"
            class="font-bold text-white force-white-text"
            :sub-content="subRouter?.meta?.subTitle"
            :content="subRouter?.meta?.title || subRouter?.meta?.name"
          />
        </router-link>
      </li>
    </ul>
  </li>
</template>

<script setup lang="ts">
import { computed, PropType, ComputedRef } from 'vue';
import { RouteRecordRaw } from 'vue-router';
import { get, useToggle } from '@vueuse/core';
import { isEmpty } from 'lodash-es';
import { SvgIcon, Text } from '@tencent/aix-design';

// 组件Props定义
const props = defineProps({
  // 路由配置对象，包含路由信息和元数据
  router: {
    type: Object as PropType<RouteRecordRaw>,
    default: () => {},
  },
  // 当前激活的路由名称，用于高亮显示
  active: {
    type: String,
    default: '',
  },
});

// 展开/收缩状态管理：默认展开(true)
const [expended, toggleExpended] = useToggle(true);

/**
 * 计算属性：过滤出可见的子路由
 * 过滤条件：
 * 1. 不隐藏 (!r?.meta?.hide)
 * 2. PC端不隐藏 (!r?.meta?.isHidePc)
 * 3. 有标题或名称 (r?.meta?.title || r?.meta?.name)
 */
const visibleChildren: ComputedRef<
  RouteRecordRaw[] &
    {
      meta?: {
        subTitle?: string;
      };
    }[]
> = computed(() =>
  (props.router.children || []).filter(
    (r) => !r?.meta?.hide && !r?.meta?.isHidePc && (r?.meta?.title || r?.meta?.name),
  ),
);

/**
 * 计算属性：判断是否显示子菜单
 * 基于visibleChildren是否为空来决定
 */
const showChildren = computed(() => !isEmpty(get(visibleChildren)));
</script>

<style scoped lang="scss">
/* 导航项基础样式 - 黑色背景，白色文本 */
.nav-item {
  @apply w-[100%] min-h-[32px] flex items-center px-[8px] py-[4px] select-none rounded-default;
  background-color: #000000; /* 默认黑色背景 */
  color: #ffffff !important; /* 默认白色文本 */
}

/* 导航项悬停效果 - 深灰色背景 */
.nav-item:hover {
  /* 降低 alpha 值（0.4 比 0.6 更透明） */
  background: linear-gradient(110.28deg, rgba(153, 98, 158, 0.4) 0.2%, rgba(49, 82, 139, 0.4) 101.11%);
  box-shadow: 23px 23px 34px rgba(0, 0, 0, 0.05);
  backdrop-filter: blur(25px);
  border-color: transparent;

  color: #ffffff; /* 保持白色文本 */
}

/* 子菜单项左边距样式 */
.nav-item.pl-\[32px\] {
  padding-left: 32px;
}

/* 激活路由的背景样式 - 深蓝色背景 */
.router-link-active {
  background: linear-gradient(90deg, #c67be7 0%, #4d6afb 100%);
  box-shadow: 0 2px 8px rgba(198, 123, 231, 0.3);
  color: #ffffff !important; /* 激活状态白色文本 */
  @apply rounded-default;
}

/* 确保图标也是白色 */
.nav-item .t-icon {
  color: #ffffff !important;
}

/* 确保SVG图标是白色 */
.nav-item .svg-icon {
  color: #ffffff !important;
  fill: #ffffff !important;
}

/* 强制白色文本类 - 针对Text组件的特殊处理 */
.force-white-text {
  color: #ffffff !important;
}

/* Text组件样式穿透 - 多层级深度穿透 */
:deep(.force-white-text) {
  color: #ffffff !important;
}

:deep(.force-white-text *) {
  color: #ffffff !important;
}

:deep(.force-white-text .t-text) {
  color: #ffffff !important;
}

:deep(.force-white-text .t-text *) {
  color: #ffffff !important;
}

:deep(.force-white-text .t-text__content) {
  color: #ffffff !important;
}

:deep(.force-white-text .t-text__ellipsis) {
  color: #ffffff !important;
}

:deep(.force-white-text .t-text__tooltip) {
  color: #ffffff !important;
}

/* 通用Text组件样式穿透 - 更强的优先级 */
:deep(.t-text) {
  color: #ffffff !important;
}

:deep(.t-text *) {
  color: #ffffff !important;
}

:deep(.t-text .t-text__content) {
  color: #ffffff !important;
}

:deep(.t-text .t-text__ellipsis) {
  color: #ffffff !important;
}

:deep(.t-text .t-text__tooltip) {
  color: #ffffff !important;
}

/* 针对TDesign Text组件的所有可能类名进行穿透 */
:deep(.t-text--nav-title) {
  color: #ffffff !important;
}

:deep(.t-text--normal) {
  color: #ffffff !important;
}

:deep(.t-text--small) {
  color: #ffffff !important;
}

:deep(.t-text--large) {
  color: #ffffff !important;
}

/* 最强优先级 - 覆盖所有可能的Text组件样式 */
:deep([class*='t-text']) {
  color: #ffffff !important;
}

:deep([class*='t-text'] *) {
  color: #ffffff !important;
}

/* 针对可能的内联样式进行覆盖 */
:deep(.force-white-text[style*='color']) {
  color: #ffffff !important;
}

:deep(.t-text[style*='color']) {
  color: #ffffff !important;
}
</style>
